import { infiniteQueryOptions, keepPreviousData, queryOptions } from '@tanstack/react-query';
import type { EFileStatus, RecordStatus } from '@tabservice/tabfile-client';
import { tabfile } from './api';

export interface FilingsFilter {
  status?: EFileStatus;
  formType?: string;
  taxYear?: number;
  /** Free text, searched by Tab Service. */
  q?: string;
  /**
   * Which column the table is ordered by, as `created` or `records:asc`.
   *
   * Sent to Tab Service rather than applied here: a page holds ten of several hundred e-files,
   * and sorting those ten would put the oldest of this page above the newest of the next.
   */
  sort?: string;
  limit?: number;
  cursor?: string;
}

/** The records filter by fix state, including the "needs attention" question. */
export type FixStateFilter = 'attention' | 'none' | 'queued' | 'submitted' | 'corrected' | 'fix_failed';

export const queryKeys = {
  me: ['me'] as const,
  forms: ['forms'] as const,
  filings: (filter: FilingsFilter) => ['e-files', filter] as const,
  filing: (filingId: string) => ['e-file', filingId] as const,
  filingRecords: (filingId: string, status?: RecordStatus) => ['e-file-records', filingId, status ?? 'all'] as const,
  filingRecord: (filingId: string, recordRef: string) => ['e-file-record', filingId, recordRef] as const,
  formSchema: (formType: string) => ['form-schema', formType] as const,
  upload: (uploadId: string) => ['upload', uploadId] as const,
  keys: ['keys'] as const,
  webhooks: ['webhooks'] as const,
  webhookDeliveries: (webhookId: string) => ['webhook-deliveries', webhookId] as const,
  users: ['users'] as const,
  adminCustomers: ['admin-customers'] as const,
  adminCustomer: (customerId: string) => ['admin-customer', customerId] as const,
  adminStaff: ['admin-staff'] as const,
  adminUsage: (month: string) => ['admin-usage', month] as const,
};

export const meQuery = () =>
  queryOptions({
    queryKey: queryKeys.me,
    queryFn: () => tabfile.GET('/v1/me'),
    staleTime: 60_000,
  });

export const formsQuery = () =>
  queryOptions({
    queryKey: queryKeys.forms,
    queryFn: () => tabfile.GET('/v1/forms'),
    staleTime: 5 * 60_000,
  });

export const filingsQuery = (filter: FilingsFilter = {}) =>
  queryOptions({
    queryKey: queryKeys.filings(filter),
    queryFn: () =>
      tabfile.GET('/v1/e-files', {
        params: {
          query: {
            status: filter.status,
            formType: filter.formType,
            taxYear: filter.taxYear,
            q: filter.q || undefined,
            limit: filter.limit ?? 100,
            cursor: filter.cursor,
          },
        },
      }),
  });

/**
 * The e-files list, a page at a time. Tab Service searches and counts; the UI turns the
 * pages it has fetched into page numbers, the same way the records table does.
 */
export const filingQuery = (filingId: string) =>
  queryOptions({
    queryKey: queryKeys.filing(filingId),
    queryFn: () => tabfile.GET('/v1/e-files/{efileId}', { params: { path: { efileId: filingId } } }),
  });

/**
 * Why the records of this e-file failed, grouped.
 *
 * Its own request rather than part of the e-file, because an e-file with nothing wrong with it
 * has nothing to group: the page asks only when there is something to ask about.
 */
export const filingErrorSummaryQuery = (filingId: string, enabled: boolean) =>
  queryOptions({
    queryKey: ['e-file-error-summary', filingId] as const,
    queryFn: () =>
      tabfile.GET('/v1/e-files/{efileId}/error-summary', { params: { path: { efileId: filingId } } }),
    enabled,
  });

export const filingRecordsQuery = (filingId: string, status: RecordStatus | undefined, cursor?: string) =>
  queryOptions({
    queryKey: [...queryKeys.filingRecords(filingId, status), cursor ?? 'first'] as const,
    queryFn: () =>
      tabfile.GET('/v1/e-files/{efileId}/records', {
        params: { path: { efileId: filingId }, query: { status, cursor, limit: 25 } },
      }),
  });

/**
 * The same records endpoint, paged. Tab Service hands back a cursor; the UI turns
 * the pages it has fetched into page numbers.
 */
/**
 * One record with its data, its IRS result and its fix state. Only asked for
 * when the drawer is open, because it is the record being worked on.
 */
export const filingRecordQuery = (filingId: string, recordRef: string | null) =>
  queryOptions({
    queryKey: queryKeys.filingRecord(filingId, recordRef ?? 'none'),
    queryFn: () =>
      tabfile.GET('/v1/e-files/{efileId}/records/{recordRef}', {
        params: { path: { efileId: filingId, recordRef: recordRef as string } },
      }),
    enabled: recordRef !== null,
    staleTime: 0,
  });

/**
 * The JSON Schema of one canonical record. It is what the record drawer reads
 * for field order, labels and types, so a form gaining a box needs no UI change.
 */
export const formSchemaQuery = (formType: string | undefined) =>
  queryOptions({
    queryKey: queryKeys.formSchema(formType ?? 'none'),
    queryFn: () =>
      tabfile.GET('/v1/forms/{formType}/schema', { params: { path: { formType: formType as string } } }),
    enabled: formType !== undefined,
    staleTime: 5 * 60_000,
  });

export const uploadQuery = (uploadId: string) =>
  queryOptions({
    queryKey: queryKeys.upload(uploadId),
    queryFn: () => tabfile.GET('/v1/uploads/{uploadId}', { params: { path: { uploadId } } }),
    staleTime: 0,
  });

export const keysPageQuery = (page: number, pageSize: number, sort?: string) =>
  queryOptions({
    queryKey: [...queryKeys.keys, 'page', page, pageSize, sort ?? ''] as const,
    queryFn: () =>
      tabfile.GET('/v1/keys', { params: { query: { page, limit: pageSize, sort } } }),
    placeholderData: keepPreviousData,
  });

export const webhooksPageQuery = (page: number, pageSize: number) =>
  queryOptions({
    queryKey: [...queryKeys.webhooks, 'page', page, pageSize] as const,
    queryFn: () => tabfile.GET('/v1/webhooks', { params: { query: { page, limit: pageSize } } }),
    placeholderData: keepPreviousData,
  });

export const webhookDeliveriesPageQuery = (
  webhookId: string,
  page: number,
  pageSize: number,
  enabled: boolean,
) =>
  queryOptions({
    queryKey: [...queryKeys.webhookDeliveries(webhookId), 'page', page, pageSize] as const,
    queryFn: () =>
      tabfile.GET('/v1/webhooks/{webhookId}/deliveries', {
        params: { path: { webhookId }, query: { page, limit: pageSize } },
      }),
    enabled,
    placeholderData: keepPreviousData,
  });

export const usersPageQuery = (page: number, pageSize: number, sort?: string) =>
  queryOptions({
    queryKey: [...queryKeys.users, 'page', page, pageSize, sort ?? ''] as const,
    queryFn: () =>
      tabfile.GET('/v1/users', { params: { query: { page, limit: pageSize, sort } } }),
    placeholderData: keepPreviousData,
  });

export const adminCustomersQuery = () =>
  queryOptions({
    queryKey: queryKeys.adminCustomers,
    queryFn: () => tabfile.GET('/v1/admin/customers'),
  });

export const adminCustomerQuery = (customerId: string) =>
  queryOptions({
    queryKey: queryKeys.adminCustomer(customerId),
    queryFn: () => tabfile.GET('/v1/admin/customers/{customerId}', { params: { path: { customerId } } }),
  });

export const adminCustomerKeysPageQuery = (customerId: string, page: number, pageSize: number) =>
  queryOptions({
    queryKey: [...queryKeys.adminCustomer(customerId), 'keys', page, pageSize] as const,
    queryFn: () =>
      tabfile.GET('/v1/admin/customers/{customerId}/keys', {
        params: { path: { customerId }, query: { page, limit: pageSize } },
      }),
    placeholderData: keepPreviousData,
  });

export const adminCustomerUsersPageQuery = (customerId: string, page: number, pageSize: number) =>
  queryOptions({
    queryKey: [...queryKeys.adminCustomer(customerId), 'users', page, pageSize] as const,
    queryFn: () =>
      tabfile.GET('/v1/admin/customers/{customerId}/users', {
        params: { path: { customerId }, query: { page, limit: pageSize } },
      }),
    placeholderData: keepPreviousData,
  });

export const adminStaffPageQuery = (page: number, pageSize: number, sort?: string) =>
  queryOptions({
    queryKey: [...queryKeys.adminStaff, 'page', page, pageSize, sort ?? ''] as const,
    queryFn: () =>
      tabfile.GET('/v1/admin/staff', { params: { query: { page, limit: pageSize, sort } } }),
    placeholderData: keepPreviousData,
  });

export const adminUsagePageQuery = (month: string, page: number, pageSize: number) =>
  queryOptions({
    queryKey: [...queryKeys.adminUsage(month), 'page', page, pageSize] as const,
    queryFn: () =>
      tabfile.GET('/v1/admin/usage', { params: { query: { month, page, limit: pageSize } } }),
    placeholderData: keepPreviousData,
  });

/** True when the signed in user is Tab Service staff. */
export function isStaff(me: { auth?: { user?: { role?: string } } } | undefined): boolean {
  return me?.auth?.user?.role === 'staff';
}

/** True when the signed-in identity belongs to a customer. Tab Service staff usually do not. */
export function hasCustomer(me: { customer?: unknown } | undefined): boolean {
  return me?.customer != null;
}

/** Where this person starts: customers on their e-files, staff without a customer in the admin area. */
export function homePath(me: { customer?: unknown; auth?: { user?: { role?: string } } } | undefined): '/e-files' | '/admin' {
  return hasCustomer(me) || !isStaff(me) ? '/e-files' : '/admin';
}

// ---------------------------------------------------------------------------
// Page-number queries: what a table shows is exactly one page Tab Service returned, with
// the total. The previous page stays on screen while the next one loads.

export const filingsPageQuery = (filter: FilingsFilter & { page: number; pageSize: number }) =>
  queryOptions({
    queryKey: [...queryKeys.filings(filter), 'page'] as const,
    queryFn: () =>
      tabfile.GET('/v1/e-files', {
        params: {
          query: {
            status: filter.status,
            formType: filter.formType,
            taxYear: filter.taxYear,
            q: filter.q || undefined,
            sort: filter.sort || undefined,
            page: filter.page,
            limit: filter.pageSize,
          },
        },
      }),
    placeholderData: keepPreviousData,
  });

export const filingRecordsPageQuery = (
  filingId: string,
  status: RecordStatus | undefined,
  fixState: FixStateFilter | undefined,
  page: number,
  pageSize: number,
  errorCode?: string,
  /** Reference or recipient, matched by the database over the whole e-file. */
  q?: string,
) =>
  queryOptions({
    queryKey: [
      ...queryKeys.filingRecords(filingId, status),
      fixState ?? 'any',
      errorCode ?? 'any-reason',
      'page',
      page,
      pageSize,
      q ?? '',
    ] as const,
    queryFn: () =>
      tabfile.GET('/v1/e-files/{efileId}/records', {
        params: {
          path: { efileId: filingId },
          query: { status, fixState, errorCode, page, limit: pageSize, q: q || undefined },
        },
      }),
    placeholderData: keepPreviousData,
  });

export const adminCustomersPageQuery = (q: string, page: number, pageSize: number) =>
  queryOptions({
    queryKey: [...queryKeys.adminCustomers, 'page', q, page, pageSize] as const,
    queryFn: () =>
      tabfile.GET('/v1/admin/customers', { params: { query: { q: q || undefined, page, limit: pageSize } } }),
    placeholderData: keepPreviousData,
  });

/**
 * Uploads that have not become an e-file yet: the drafts on the E-files page.
 *
 * Paged. It used to ask for page 1 with a limit of 50 and show whatever came back, so the
 * fifty-first draft pushed the oldest one out of sight and the tab looked as though adding a
 * draft had deleted one. Nothing was ever deleted; the rest of the list was simply never
 * asked for.
 */
export const draftUploadsQuery = (taxYear?: number, page = 1, pageSize = 10) =>
  queryOptions({
    queryKey: ['uploads', 'unfiled', taxYear ?? 'all', page, pageSize] as const,
    queryFn: () =>
      tabfile.GET('/v1/uploads', { params: { query: { unfiled: true, taxYear, page, limit: pageSize } } }),
    placeholderData: keepPreviousData,
  });

/**
 * The records of an e-file walked page by page with a cursor: what the correction and
 * replacement panels use to read every record of the parent.
 */
export const filingRecordsPagesQuery = (
  filingId: string,
  status: RecordStatus | undefined,
  fixState?: FixStateFilter,
) =>
  infiniteQueryOptions({
    queryKey: [...queryKeys.filingRecords(filingId, status), fixState ?? 'any', 'pages'] as const,
    queryFn: ({ pageParam }) =>
      tabfile.GET('/v1/e-files/{efileId}/records', {
        params: { path: { efileId: filingId }, query: { status, fixState, cursor: pageParam, limit: 25 } },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
